import { useState, useCallback } from 'react';

export function useForm(initialValues = {}, onSubmit) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear field error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  }, [errors]);

  const setValue = useCallback((name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const validate = useCallback((validationRules = {}) => {
    const newErrors = {};

    Object.entries(validationRules).forEach(([field, rules]) => {
      const value = values[field];

      if (rules.required && (!value || value.trim() === '')) {
        newErrors[field] = rules.message || `${field} is required`;
      } else if (rules.email && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        newErrors[field] = 'Please enter a valid email address';
      } else if (rules.minLength && value && value.length < rules.minLength) {
        newErrors[field] = `Must be at least ${rules.minLength} characters`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [values]);

  const handleSubmit = useCallback(async (e, validationRules) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    if (validationRules && !validate(validationRules)) {
      return false;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(values);
      setSubmitSuccess(true);
      return true;
    } catch (err) {
      setSubmitError(err.message);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [values, validate, onSubmit]);

  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setSubmitError(null);
    setSubmitSuccess(false);
  }, [initialValues]);

  return {
    values,
    errors,
    isSubmitting,
    submitError,
    submitSuccess,
    handleChange,
    setValue,
    validate,
    handleSubmit,
    reset,
  };
}

export default useForm;
